import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3,
  TrendingUp,
  Activity,
  Target,
  Users,
  Mail,
  MousePointer,
  MessageSquare,
  Calendar,
  Clock,
  Zap,
  Eye,
  Filter,
  Download,
  RefreshCw,
  Settings,
  AlertCircle,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card';
import { cn } from '../../../../utils/cn';

// Import our analytics components
import CampaignMetrics from '../../../email/analytics/CampaignMetrics';
import RecipientJourney from '../../../email/analytics/RecipientJourney';
import EngagementHeatmap from '../../../email/analytics/EngagementHeatmap';

interface AnalyticsTabProps {
  campaignData: any;
  onDataChange: (field: string, value: any) => void;
}

interface MetricCard {
  title: string;
  value: string | number;
  change: number;
  changeType: 'increase' | 'decrease' | 'neutral';
  icon: React.ElementType;
  color: string;
  description: string;
}

const mockMetrics: MetricCard[] = [
  {
    title: 'Total Emails Sent',
    value: '24,567',
    change: 15.2,
    changeType: 'increase',
    icon: Mail,
    color: 'bg-blue-500',
    description: 'Emails sent across all campaigns'
  },
  {
    title: 'Open Rate',
    value: '68.4%',
    change: 8.7,
    changeType: 'increase',
    icon: Eye,
    color: 'bg-green-500',
    description: 'Average email open rate'
  },
  {
    title: 'Click Rate',
    value: '24.1%',
    change: -2.3,
    changeType: 'decrease',
    icon: MousePointer,
    color: 'bg-purple-500',
    description: 'Email click-through rate'
  },
  {
    title: 'Reply Rate',
    value: '12.8%',
    change: 4.5,
    changeType: 'increase',
    icon: MessageSquare,
    color: 'bg-orange-500',
    description: 'Email response rate'
  },
  {
    title: 'Engagement Score',
    value: '78',
    change: 0,
    changeType: 'neutral',
    icon: Activity,
    color: 'bg-indigo-500',
    description: 'Overall engagement score'
  },
  {
    title: 'Conversion Rate',
    value: '3.2%',
    change: 12.1,
    changeType: 'increase',
    icon: Target,
    color: 'bg-emerald-500',
    description: 'Lead to customer conversion'
  }
];

export const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ campaignData, onDataChange }) => {
  const [activeView, setActiveView] = useState<'overview' | 'performance' | 'journey' | 'heatmap'>('overview');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');
  const [isLoading, setIsLoading] = useState(false);

  const getChangeIcon = (changeType: string) => {
    switch (changeType) {
      case 'increase':
        return <ArrowUp className="w-4 h-4 text-green-500" />;
      case 'decrease':
        return <ArrowDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-neutral-500" />;
    }
  };

  const getChangeColor = (changeType: string) => {
    switch (changeType) {
      case 'increase':
        return 'text-green-600 dark:text-green-400';
      case 'decrease':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-neutral-600 dark:text-neutral-400';
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Key Metrics Grid */}
      <div className="grid grid-cols-3 gap-6">
        {mockMetrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <Card key={metric.title} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center", metric.color)}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center space-x-1">
                  {getChangeIcon(metric.changeType)}
                  <span className={cn("text-sm font-medium", getChangeColor(metric.changeType))}>
                    {metric.change !== 0 && `${metric.change > 0 ? '+' : ''}${metric.change}%`}
                    {metric.change === 0 && 'No change'}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-1">
                  {metric.value}
                </h3>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {metric.title}
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  {metric.description}
                </p>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Quick Insights */}
      <div className="grid grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Performance Insights
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2" />
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Email open rates increased by 8.7%
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Best performing time: Tuesday 10 AM
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2" />
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Mobile engagement up 23%
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Mobile users show higher click rates
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full mt-2" />
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Subject line A/B test winner
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  "Quick question" outperformed by 15%
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Recommendations
          </h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Optimize send times
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Send emails between 9-11 AM for best results
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-orange-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Improve click rates
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Add more compelling CTAs in email body
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Zap className="w-5 h-5 text-purple-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  Segment high-value prospects
                </p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">
                  Create targeted campaigns for engaged users
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Recent Activity
        </h3>
        <div className="space-y-4">
          {[
            { time: '2 hours ago', event: 'Campaign "Q4 Launch" achieved 75% open rate', type: 'success' },
            { time: '4 hours ago', event: 'New high-value prospect identified: Sarah Johnson', type: 'info' },
            { time: '6 hours ago', event: 'A/B test completed with 15% improvement', type: 'success' },
            { time: '1 day ago', event: 'Email deliverability issue detected and resolved', type: 'warning' },
            { time: '2 days ago', event: 'Monthly analytics report generated', type: 'info' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center space-x-4 p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
              <div className={cn(
                "w-2 h-2 rounded-full",
                activity.type === 'success' ? 'bg-green-500' :
                activity.type === 'warning' ? 'bg-orange-500' : 'bg-blue-500'
              )} />
              <div className="flex-1">
                <p className="text-sm text-neutral-900 dark:text-neutral-100">{activity.event}</p>
                <p className="text-xs text-neutral-600 dark:text-neutral-400">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );

  const renderPerformance = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Campaign Performance Metrics
          </h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Filter
            </Button>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        <CampaignMetrics />
      </Card>
    </div>
  );

  const renderJourney = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Recipient Journey Analysis
          </h3>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Settings className="w-4 h-4 mr-2" />
              Configure
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
        <RecipientJourney />
      </Card>
    </div>
  );

  const renderHeatmap = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            Engagement Heatmap
          </h3>
          <div className="flex items-center space-x-2">
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="px-3 py-1 border border-neutral-200 dark:border-neutral-700 rounded-md text-sm bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
            <Button variant="outline" size="sm">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>
        <EngagementHeatmap campaignId={campaignData?.id || 'default'} />
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            Campaign Analytics
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            Comprehensive insights into your campaign performance and engagement
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" size="sm" onClick={() => setIsLoading(true)}>
            <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
            Refresh Data
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center space-x-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'performance', label: 'Performance', icon: TrendingUp },
          { id: 'journey', label: 'Journey', icon: Users },
          { id: 'heatmap', label: 'Heatmap', icon: Activity }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                activeView === tab.id
                  ? "bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeView === 'overview' && renderOverview()}
        {activeView === 'performance' && renderPerformance()}
        {activeView === 'journey' && renderJourney()}
        {activeView === 'heatmap' && renderHeatmap()}
      </motion.div>
    </div>
  );
}; 